import React from 'react';
import { Link } from 'react-router-dom';
import { FaHome, FaChevronRight, FaWrench, FaBolt, FaClock, FaCheckCircle, FaArrowRight, FaPhone, FaLaptop, FaServer, FaNetworkWired } from 'react-icons/fa';
import Navigation from '../components/Navigation';
import TopBar from '../components/TopBar';
import Footer from '../components/Footer';

const BreakFixPage = () => {
    const services = [
        {
            icon: <FaLaptop size={24} />,
            title: 'Workstation Repair',
            description: 'Fast diagnosis and repair of desktops, laptops, and all-in-one computers.',
            gradient: 'from-amber-500 to-orange-500'
        },
        {
            icon: <FaServer size={24} />,
            title: 'Server Repair',
            description: 'Critical server hardware repairs with rapid response times to minimize downtime.',
            gradient: 'from-red-500 to-rose-500'
        },
        {
            icon: <FaNetworkWired size={24} />,
            title: 'Network Equipment',
            description: 'Repair and replacement of switches, routers, access points, and network devices.',
            gradient: 'from-blue-600 to-cyan-600'
        },
        {
            icon: <FaBolt size={24} />,
            title: 'Rapid Response',
            description: 'Priority dispatch for urgent repairs with same-day service available.',
            gradient: 'from-purple-600 to-indigo-600'
        },
        {
            icon: <FaWrench size={24} />,
            title: 'Component Replacement',
            description: 'Quick replacement of failed components including drives, memory, and power supplies.',
            gradient: 'from-teal-500 to-green-500'
        },
        {
            icon: <FaClock size={24} />,
            title: 'Minimal Downtime',
            description: 'Efficient repair processes designed to get your systems back online fast.',
            gradient: 'from-orange-500 to-amber-500'
        }
    ];

    const benefits = [
        'No long-term contracts required',
        'Pay only for what you need',
        'Certified repair technicians',
        'Parts and labor warranty',
        'On-site or depot repair options',
        'Rapid turnaround times'
    ];

    return (
        <div className="font-sans">
            <TopBar />
            <Navigation />

            {/* Hero Section */}
            <section className="relative bg-gradient-to-br from-gray-900 via-amber-900 to-orange-900 py-24 overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute inset-0" style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                    }}></div>
                </div>

                <div className="absolute top-0 right-0 w-96 h-96 bg-amber-500 rounded-full filter blur-3xl opacity-20 translate-x-1/2 -translate-y-1/2"></div>
                <div className="absolute bottom-0 left-0 w-96 h-96 bg-orange-500 rounded-full filter blur-3xl opacity-20 -translate-x-1/2 translate-y-1/2"></div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <nav className="flex items-center gap-2 text-sm text-gray-300 mb-8">
                        <Link to="/" className="flex items-center gap-1 hover:text-white transition-colors">
                            <FaHome size={14} />
                            <span>Home</span>
                        </Link>
                        <FaChevronRight size={10} className="text-gray-500" />
                        <span className="text-amber-400">Break & Fix</span>
                    </nav>

                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                                Rapid <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-400">Break & Fix</span> Services
                            </h1>
                            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                                When IT equipment fails, you need fast, reliable repairs. Our break/fix services get you back up and running quickly.
                            </p>
                            <div className="flex flex-wrap gap-4">
                                <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-amber-500/30 transition-all duration-300">
                                    Request Repair
                                    <FaArrowRight size={14} />
                                </Link>
                                <a href="tel:+12014099845" className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white/30 text-white rounded-xl font-semibold hover:bg-white/10 transition-all duration-300">
                                    <FaPhone size={14} />
                                    Call Us Now
                                </a>
                            </div>
                        </div>
                        <div className="relative">
                            <div className="bg-gradient-to-br from-amber-600/20 to-orange-600/20 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
                                <img
                                    src="https://images.unsplash.com/photo-1581092160562-40aa08e78837?w=600&h=400&fit=crop"
                                    alt="Break Fix Repair"
                                    className="w-full h-80 object-cover rounded-xl shadow-2xl"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Services Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <span className="inline-block px-4 py-2 bg-amber-100 text-amber-700 rounded-full text-sm font-semibold mb-4">Our Services</span>
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Fast IT Repair Services</h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Quick, reliable repairs for all your IT equipment when you need them most
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {services.map((service, index) => (
                            <div
                                key={index}
                                className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-amber-200"
                            >
                                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${service.gradient} flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform duration-300`}>
                                    {service.icon}
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                                <p className="text-gray-600 leading-relaxed">{service.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Benefits Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-amber-50">
                <div className="max-w-7xl mx-auto">
                    <div className="grid lg:grid-cols-2 gap-16 items-center">
                        <div>
                            <img
                                src="https://images.unsplash.com/photo-1597872200969-2b65d56bd16b?w=600&h=500&fit=crop"
                                alt="IT Repair"
                                className="w-full h-auto rounded-2xl shadow-2xl"
                            />
                        </div>
                        <div>
                            <span className="inline-block px-4 py-2 bg-amber-100 text-amber-700 rounded-full text-sm font-semibold mb-4">Why Choose Us</span>
                            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Hassle-Free IT Repairs</h2>
                            <p className="text-lg text-gray-600 mb-8">
                                No contracts, no hassle. Just fast, professional repairs when your equipment breaks down.
                            </p>
                            <div className="grid grid-cols-2 gap-4">
                                {benefits.map((benefit, index) => (
                                    <div key={index} className="flex items-center gap-3">
                                        <FaCheckCircle className="text-amber-500 flex-shrink-0" size={20} />
                                        <span className="text-gray-700 font-medium">{benefit}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-amber-500 to-orange-500">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Equipment Down? We Can Help!</h2>
                    <p className="text-xl text-amber-100 mb-8">
                        Get fast, professional IT repairs with our break/fix services.
                    </p>
                    <div className="flex flex-wrap justify-center gap-4">
                        <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-4 bg-white text-amber-600 rounded-xl font-semibold hover:shadow-lg transition-all duration-300">
                            Submit Repair Request
                            <FaArrowRight size={14} />
                        </Link>
                        <a href="tel:+12014099845" className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white text-white rounded-xl font-semibold hover:bg-white/10 transition-all duration-300">
                            <FaPhone size={14} />
                            Call Us Now
                        </a>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default BreakFixPage;
